import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { AssetClassesService } from 'src/app/services/asset-classes.service';
import { AssetClass } from '@models/asset-class.interface';
import { SaveAssetClassDto } from '@models/save-asset-class-dto.interface';

export interface AssetClassSavedEvent {
  id: number;
}

@Component({
  selector: 'app-asset-class-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './asset-class-detail.component.html',
  styleUrl: './asset-class-detail.component.scss'
})
export class AssetClassDetailComponent implements OnChanges, OnDestroy {
  @Input() assetClass: AssetClass | null = null;

  @Output() saved = new EventEmitter<AssetClassSavedEvent>();

  private readonly assetClassesService = inject(AssetClassesService);
  private readonly fb = inject(FormBuilder);
  private saveRequest?: Subscription;

  protected isSaving = signal(false);
  protected errorMessage = signal('');

  protected form = this.fb.group({
    name:  [null as string | null, [Validators.required, Validators.maxLength(200)]],
    alias: [null as string | null, Validators.maxLength(50)]
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['assetClass']) return;

    if (this.assetClass) {
      this.form.reset();
      this.form.patchValue({
        name:  this.assetClass.name ?? '',
        alias: this.assetClass.alias
      }, { emitEvent: false });
    } else {
      this.form.reset();
    }

    this.errorMessage.set('');
    this.form.enable();
  }

  ngOnDestroy(): void {
    this.saveRequest?.unsubscribe();
  }

  get isNew(): boolean {
    return !this.assetClass || this.assetClass.id === 0;
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]), select[formControlName]:not([disabled])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  protected onSave(): void {
    if (this.form.invalid || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SaveAssetClassDto = {
      name:  raw.name!.trim(),
      alias: raw.alias?.trim() ?? null
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.clearServerErrors();

    const request$ = this.isNew
      ? this.assetClassesService.create(dto)
      : this.assetClassesService.update(this.assetClass!.id, dto);

    this.saveRequest = request$.subscribe({
      next: savedAssetClass => {
        this.isSaving.set(false);
        this.saved.emit({ id: savedAssetClass.id });
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.handleServerError(err);
      }
    });
  }

  private handleServerError(err: HttpErrorResponse): void {
    const body = err.error;

    if (body?.errors && typeof body.errors === 'object') {
      const errors = body.errors as Record<string, string[]>;

      let mapped = false;
      for (const [serverField, messages] of Object.entries(errors)) {
        const controlName = Object.keys(this.form.controls)
          .find(key => key.toLowerCase() === serverField.toLowerCase());
        const control = controlName ? this.form.get(controlName) : null;
        if (control) {
          control.setErrors({ serverError: messages[0] });
          control.markAsTouched();
          mapped = true;
        }
      }

      if (!mapped) {
        this.errorMessage.set('Save failed. Please check your entries and try again.');
      }
    } else {
      this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  private clearServerErrors(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.hasError('serverError')) {
        const { serverError: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
  }
}